/**
 * The domain, in the words the business uses.
 *
 * The chain the whole app exists to represent:
 *
 *   María Rodríguez        (Party — the one who pays)
 *     ↓ paga por
 *   Juan Rodríguez         (Member — the one who receives the service)
 *     ↓ inscrito en
 *   Fútbol U12             (Plan — B/.35 al mes, vence el día 5)
 *     ↓ genera
 *   Cobro de agosto        (Obligation — one period of money expected)
 *     ↓ se salda con
 *   Pago por Yappy         (Payment)
 *
 * A period is a month or an ISO week, depending on the plan; "cobro" is the
 * word for both, and it is the word the screen and the route already use.
 *
 * Party and Member are separate even when they are the same human, because
 * the parent/child case is the common one in every vertical we are testing
 * and collapsing it would mean modelling it back later.
 *
 * All amounts are integer cents. All dates are local calendar strings.
 */
/**
 * What `app.party.tax_id_kind` may hold, across the nine countries.
 * `PASAPORTE` is the one every country shares; the rest are the local document each one actually
 * uses, per `taxIdKindsFor` in `locale.ts`. Kept as ONE flat list rather than
 * a value per country, so a party's `tax_id_kind` stays a single column and a
 * single check constraint no matter how many countries this grows to.
 */
export const TAX_ID_KINDS = [
    "RUC",
    "CEDULA",
    "CEDULA_JURIDICA",
    "PASAPORTE",
    "NIT",
    "DPI",
    "RFC",
    "CURP",
    "DNI",
    "RUT",
    "RIF",
    "SSN",
    "EIN",
];
/**
 * DOCUMENTS ONLY A COMPANY HOLDS. An employee is a natural person, so these
 * are never offered for one. RUC, NIT, RFC, RIF and RUT stay: a natural person
 * holds them too.
 */
export const COMPANY_ONLY_TAX_ID_KINDS = ["CEDULA_JURIDICA", "EIN"];
/** What a kind is called on screen. User-facing, so Spanish, and one spelling for the whole app. */
export const TAX_ID_KIND_LABELS = {
    RUC: "RUC",
    CEDULA: "Cédula",
    CEDULA_JURIDICA: "Cédula jurídica",
    PASAPORTE: "Pasaporte",
    NIT: "NIT",
    DPI: "DPI",
    RFC: "RFC",
    CURP: "CURP",
    DNI: "DNI",
    RUT: "RUT",
    RIF: "RIF",
    SSN: "SSN",
    EIN: "EIN",
};
/**
 * THE UNION across every country on the list — what the server accepts and
 * the two CHECK constraints allow (migration 0025). Which of these a screen
 * offers is the country's call: see payment-methods.ts.
 */
export const PAYMENT_METHODS = [
    "YAPPY",
    "NEQUI",
    "DAVIPLATA",
    "SINPE_MOVIL",
    "YAPE",
    "PLIN",
    "PAGO_MOVIL",
    "ZELLE",
    "VENMO",
    "BANK_TRANSFER",
    "CASH",
    "CARD",
    "CHECK",
    "OTHER",
];
/** The enum never reaches a screen; this is what a person reads. */
export const PAYMENT_METHOD_LABELS = {
    YAPPY: "Yappy",
    NEQUI: "Nequi",
    DAVIPLATA: "Daviplata",
    SINPE_MOVIL: "SINPE Móvil",
    YAPE: "Yape",
    PLIN: "Plin",
    PAGO_MOVIL: "Pago Móvil",
    ZELLE: "Zelle",
    VENMO: "Venmo",
    BANK_TRANSFER: "Transferencia",
    CASH: "Efectivo",
    CARD: "Tarjeta",
    CHECK: "Cheque",
    OTHER: "Otro",
};
// --------------------------------------------------------------- el libro de caja
/** Money moving into the business, or out of it. Nothing else. */
export const CASH_DIRECTIONS = ["IN", "OUT"];
/**
 * The example shown in the counterparty field, by direction -- who pays the
 * business is a different kind of name than who the business pays, and one
 * example for both teaches the wrong lesson: an ingreso's placeholder must
 * not suggest a utility like Naturgy, which is somebody the business pays.
 *
 * ONE PAIR FOR BOTH CLIENTS, so a screen cannot answer this differently from
 * the other -- used by the plain «Contraparte» (an ingreso, and the correction
 * door) and by the «¿A quién le pagas?» that replaced it on an egreso. The
 * debt side («¿Quién te debe?» / «¿A quién le debes?») is its own question
 * asked while a movement is only anotada, not yet paid, and keeps its own
 * examples rather than reading this pair.
 */
export const COUNTERPARTY_PLACEHOLDERS = {
    IN: "Ej. Familia Pérez",
    OUT: "Ej. Naturgy",
};
/**
 * WHO A SIDE MAY OFFER, ONE TABLE INSTEAD OF SIX TERNARIES. «¿Quién te debe?»
 * is a customer, «¿A quién le debes?» is a supplier -- the bug of `b793db9`,
 * closed by construction rather than by a filter on the screen. Before task
 * 22b this was `direction === "IN" ? "CLIENT" : "SUPPLIER"`, written out at
 * every counterparty field in both clients; one wrong copy would have reopened
 * that bug silently, which is exactly what a shared `Record` cannot do.
 */
export const PARTY_ROLE_FOR_DIRECTION = {
    IN: "CLIENT",
    OUT: "SUPPLIER",
};
/**
 * Column width for `cash_entry.concept` and `debt.concept` — the same
 * reasoning as `MAX_AMOUNT_CENTS`: not an opinion about what a concept should
 * say, a ceiling in front of the column that holds it.
 *
 * 280 IS THE WIDER OF THE TWO FIELDS MIGRATION 0031 MERGED: a «Concepto» of
 * 120 and a «Nota» of 280 were two boxes for the same thing, and `concept`
 * holds both. Anything narrower would cut the tail off a sentence the owner
 * typed on purpose, and a row cut that way opens in an edit form that
 * refuses to save the value it was opened with. DO NOT TIDY THIS DOWN:
 * narrowing it makes an existing row uneditable.
 *
 * Same value as `MAX_NOTE_LENGTH` today, and deliberately its own constant:
 * that one still guards `order.note` and `wash.note`, which are a different
 * field on a different table and free to move without dragging this one.
 *
 * AND IT IS NOT WHAT A SAVE ACCEPTS. This is what somebody may TYPE; what
 * history may HOLD is longer, because the migration joined two full fields
 * into one line — see `MAX_MERGED_CONCEPT_LENGTH`, which is what every
 * correction door validates against.
 */
export const MAX_CONCEPT_LENGTH = 280;
/**
 * Column width for `category.name` — same reasoning as
 * `MAX_CONCEPT_LENGTH`.
 */
export const MAX_CATEGORY_NAME_LENGTH = 80;
/**
 * How many live categories one business may have ON EACH SIDE.
 *
 * A cap rather than none, because the picker is a row of buttons and a
 * hundred of them is not a picker. Twenty is roughly four times what a
 * nursery's books actually distinguish, so it bites only where somebody is
 * using categories as a notes field.
 *
 * Per direction, not per business: a business with many expense groupings
 * must not run out of room to name its income. And it counts LIVE ones --
 * archiving is the only way a category leaves the list, and counting archived
 * rows would mean an owner who reorganises twice can never create another.
 */
export const MAX_CATEGORIES_PER_DIRECTION = 20;
/**
 * Column width for `entry_request.question` and `.answer` — generous enough
 * for a real sentence, tight enough that this stays a question about one
 * movement and never a second note field.
 */
export const MAX_REQUEST_LENGTH = 500;
/** The enum never reaches a screen; this is what a person reads. */
export const REPORT_LINE_LABELS = {
    PAYMENT: "Cuota cobrada",
    INCOME: "Otro ingreso",
    EXPENSE: "Egreso",
};
/**
 * The bucket an OUT entry with no category falls into. Same reasoning as
 * `REPORT_LINE_LABELS`: the client reads this off the data instead of
 * special-casing a null `categoryId` itself, and it stays VISIBLE rather than
 * hidden — an unclassified pile someone can see is a pile someone eventually
 * classifies.
 */
export const UNCATEGORIZED_LABEL = "Sin categoría";
/**
 * The one income bucket that is not a category: the cuotas collected in the
 * period. Kept beside the categories rather than left out because in a
 * business that collects cuotas it IS the income -- a card that showed only
 * the loose IN entries would say «sin ingresos» under a headline of thousands.
 */
export const COLLECTED_DUES_LABEL = "Cuotas cobradas";
